import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ReferenceLine,
} from "recharts";
import { Plus, Clock, PoundSterling, TrendingDown, AlertTriangle, Settings2, Trash2, UserCircle2, Wallet, Filter, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import RecoveryPlaybookPanel from "@/components/app/RecoveryPlaybookPanel";
import ForecastExplainPanel from "@/components/app/ForecastExplainPanel";

// Critical drift threshold mirrors backend governance.DRIFT_THRESHOLD_CRITICAL
const DRIFT_THRESHOLD_CRITICAL = 15.0;

const today = () => new Date().toISOString().slice(0, 10);

const DLG = "rounded-lg border-[#1F2937] bg-[#111827] text-slate-100 sm:max-w-lg";
const FIELD = "rounded-md mt-1 bg-[#030712] border-[#1F2937] text-slate-100 placeholder:text-slate-600 focus:border-[#00F5D4]/50";
const LBL = "text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono";
const BTN_TEAL = "rounded-md bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-[#00F5D4] font-mono";

// ─── Margin bar visual (0-50% scale) ────────────────────────────────────────
const MarginBar = ({ projected, target }) => {
  const scale = 50;
  const projPct = Math.min(100, Math.max(0, (projected / scale) * 100));
  const targetPct = Math.min(100, Math.max(0, (target / scale) * 100));
  const isOnTrack = projected >= target - 5;
  const isCritical = projected < target - 15;
  const barColor = isCritical ? "#FF007A" : isOnTrack ? "#00C896" : "#F59E0B";
  const hatchWidth = Math.max(0, targetPct - projPct);
  return (
    <div className="relative">
      <div className="h-8 bg-[#1F2937] w-full relative overflow-hidden">
        {/* solid colour portion */}
        <div className="absolute left-0 top-0 h-full" style={{ width: `${projPct}%`, backgroundColor: barColor }} />
        {/* hatched shortfall */}
        {hatchWidth > 0 && (
          <div
            className="absolute top-0 h-full"
            style={{
              left: `${projPct}%`,
              width: `${hatchWidth}%`,
              backgroundImage: `repeating-linear-gradient(45deg, ${barColor}60 0, ${barColor}60 3px, transparent 0, transparent 50%)`,
              backgroundSize: "8px 8px",
            }}
          />
        )}
        {/* target tick */}
        <div className="absolute top-0 h-full w-0.5 bg-white/40" style={{ left: `${targetPct}%` }} />
      </div>
      <div className="flex justify-between text-[10px] font-mono text-[#6B7280] mt-1">
        <span>0%</span>
        <span>target {target}%</span>
        <span>50%</span>
      </div>
    </div>
  );
};

// ─── Format date for check-in history ────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

// ─── Main Burndown component ─────────────────────────────────────────────────
const Burndown = () => {
  const [searchParams] = useSearchParams();
  const urlProjectId = searchParams.get("project_id") || searchParams.get("project") || "";
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(urlProjectId);
  const [project, setProject] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [logs, setLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [user, setUser] = useState(null);
  const [memberFilter, setMemberFilter] = useState([]);

  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ budget_hours: 0, budget_cash: 0, burndown_start_date: today(), burndown_duration_days: 14 });

  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({ hours_spent: 1, hourly_billing_rate: 0, date_logged: today(), notes: "", task_id: "__none__", save_as_default: false });

  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ amount: 0, category: "general", description: "", date_incurred: today() });
  const [expenses, setExpenses] = useState([]);

  const [rateOpen, setRateOpen] = useState(false);
  const [rateInput, setRateInput] = useState(0);
  const [costRateInput, setCostRateInput] = useState(0);
  const [margin, setMargin] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, me] = await Promise.all([api.get("/projects"), api.get("/auth/me")]);
      setProjects(p.data);
      setUser(me.data);
      setRateInput(me.data.default_hourly_rate || 0);
      setCostRateInput(me.data.hourly_cost_rate || 0);
      setLogForm((f) => ({ ...f, hourly_billing_rate: me.data.default_hourly_rate || 0 }));
      if (p.data.length && !projectId) {
        const fromUrl = urlProjectId && p.data.find((x) => x.project_id === urlProjectId);
        setProjectId(fromUrl ? urlProjectId : p.data[0].project_id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    const filterQs = memberFilter.length ? `?user_ids=${memberFilter.join(",")}` : "";
    const [pdata, bdata, ldata, tdata, mdata, edata] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/burndown${filterQs}`),
      api.get(`/projects/${projectId}/time-logs${filterQs}`),
      api.get(`/tasks?project_id=${projectId}`),
      api.get(`/projects/${projectId}/margin-status`).catch(() => ({ data: null })),
      api.get(`/projects/${projectId}/expenses`),
    ]);
    setProject(pdata.data);
    setBurndown(bdata.data);
    setLogs(ldata.data);
    setTasks(tdata.data);
    setMargin(mdata.data);
    setExpenses(edata.data);
    setBudgetForm({
      budget_hours: pdata.data.budget_hours || 0,
      budget_cash: pdata.data.budget_cash || 0,
      burndown_start_date: pdata.data.burndown_start_date || pdata.data.start_date || today(),
      burndown_duration_days: pdata.data.burndown_duration_days || 14,
    });
  }, [projectId, memberFilter]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const saveBudget = async () => {
    try {
      await api.patch(`/projects/${projectId}`, {
        budget_hours: Number(budgetForm.budget_hours),
        budget_cash: Number(budgetForm.budget_cash),
        burndown_start_date: budgetForm.burndown_start_date,
        burndown_duration_days: Number(budgetForm.burndown_duration_days),
      });
      setBudgetOpen(false);
      toast.success("Budget saved");
      loadProject();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const logTime = async () => {
    if (!logForm.hours_spent || logForm.hours_spent <= 0) { toast.error("Hours must be > 0"); return; }
    try {
      await api.post(`/projects/${projectId}/time-logs`, {
        hours_spent: Number(logForm.hours_spent),
        hourly_billing_rate: Number(logForm.hourly_billing_rate || 0),
        date_logged: logForm.date_logged,
        notes: logForm.notes,
        task_id: logForm.task_id === "__none__" ? null : logForm.task_id,
      });
      if (logForm.save_as_default && Number(logForm.hourly_billing_rate) > 0) {
        const { data: me } = await api.patch("/auth/me", {
          default_hourly_rate: Number(logForm.hourly_billing_rate),
        });
        setUser(me);
        toast.success("Rate saved as your default");
      }
      setLogOpen(false);
      setLogForm({
        hours_spent: 1,
        hourly_billing_rate: logForm.hourly_billing_rate,
        date_logged: today(),
        notes: "",
        task_id: "__none__",
        save_as_default: false,
      });
      toast.success("Time logged");
      loadProject();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveDefaultRate = async () => {
    try {
      const { data: me } = await api.patch("/auth/me", {
        default_hourly_rate: Number(rateInput),
        hourly_cost_rate: Number(costRateInput),
      });
      setUser(me);
      setLogForm((f) => ({ ...f, hourly_billing_rate: me.default_hourly_rate || 0 }));
      setRateOpen(false);
      toast.success(`Rates saved — bill £${me.default_hourly_rate || 0}/h · cost £${me.hourly_cost_rate || 0}/h`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleMember = (uid) => {
    setMemberFilter((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const deleteLog = async (lid) => {
    if (!window.confirm("Delete this time log?")) return;
    try {
      await api.delete(`/time-logs/${lid}`);
      toast.success("Removed");
      loadProject();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const logExpense = async () => {
    const amt = Number(expenseForm.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    try {
      await api.post(`/projects/${projectId}/expenses`, {
        amount: amt,
        category: expenseForm.category,
        description: expenseForm.description,
        date_incurred: expenseForm.date_incurred,
      });
      setExpenseOpen(false);
      setExpenseForm({ amount: 0, category: "general", description: "", date_incurred: today() });
      toast.success(`Expense logged — £${amt.toFixed(2)} deducted from budget`);
      loadProject();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteExpense = async (eid) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${eid}`);
      toast.success("Expense removed");
      loadProject();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const totals = useMemo(() => {
    if (!burndown) return null;
    const hoursPct = burndown.budget_hours ? Math.round(((burndown.budget_hours - burndown.hours_remaining) / burndown.budget_hours) * 100) : 0;
    const cashPct = burndown.budget_cash ? Math.round(((burndown.budget_cash - burndown.cash_remaining) / burndown.budget_cash) * 100) : 0;
    return { hoursPct, cashPct };
  }, [burndown]);

  // ── EAC / projected margin computations ────────────────────────────────────
  const eacData = useMemo(() => {
    if (!burndown || !project) return null;
    const cost_to_date = burndown.total_cash_burned || 0;
    const pct_complete = (project.completion_pct || 0) / 100;
    const eac = pct_complete > 0 ? cost_to_date / pct_complete : cost_to_date;
    const contract_value = margin?.contract_value || project.contract_value || project.budget_cash || 0;
    const projected_margin = contract_value > 0 ? ((contract_value - eac) / contract_value) * 100 : 0;
    const target_margin = margin?.target_margin_percentage || project.target_margin_percentage || 0;
    const labour_cost = cost_to_date - (burndown.total_expenses_burned || 0);
    const expenses_cost = burndown.total_expenses_burned || 0;
    return { cost_to_date, pct_complete, eac, contract_value, projected_margin, target_margin, labour_cost, expenses_cost };
  }, [burndown, project, margin]);

  // Alert banner colour
  const bannerAlert = margin && margin.configured && eacData
    ? eacData.projected_margin < eacData.target_margin - 5
    : margin?.is_scope_creep_alert_triggered;

  return (
    <DarkPage text="text-slate-100">
      {/* Ambient teal light source */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute top-0 right-1/4 w-[420px] h-[420px] bg-[#00F5D4]/4 rounded-full blur-[170px]" />
      </div>

      <div className="relative z-10">
        <PageHeader
        backTo={`/workspace/${projectId}`}
          eyebrow="Frontline · Time & money"
          title="Dual burndown"
          description="Track hours and cash burn side-by-side across a sprint or delivery window. Every log captures the rate at the time of entry."
          actions={
            <div className="flex gap-2 flex-wrap items-center">
              <Dialog open={rateOpen} onOpenChange={setRateOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-md bg-[#111827] border-[#1F2937] text-slate-300 hover:bg-[#1F2937] hover:text-white" data-testid="open-default-rate-btn">
                    <Wallet className="w-4 h-4 mr-1" />
                    {user?.default_hourly_rate ? `My rate: £${user.default_hourly_rate}` : "Set default rate"}
                  </Button>
                </DialogTrigger>
                <DialogContent className={DLG}>
                  <DialogHeader><DialogTitle className="font-display tracking-tight text-white">My hourly rates</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">Billing rate is what you charge the client (revenue). Cost rate is what this hour costs the business (salary loaded). Cost rate drives the real-time margin guardrail.</p>
                    <div>
                      <Label className={LBL}>Billing rate — to client (£/h)</Label>
                      <Input type="number" min="0" step="5" className={FIELD} value={rateInput} onChange={(e) => setRateInput(e.target.value)} data-testid="default-rate-input" />
                    </div>
                    <div>
                      <Label className={LBL}>Cost rate — internal (£/h)</Label>
                      <Input type="number" min="0" step="5" className={FIELD} value={costRateInput} onChange={(e) => setCostRateInput(e.target.value)} data-testid="cost-rate-input" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={saveDefaultRate} className={BTN_TEAL} data-testid="confirm-default-rate">Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-md bg-[#111827] border-[#1F2937] text-slate-300 hover:bg-[#1F2937] hover:text-white" data-testid="open-budget-btn">
                    <Settings2 className="w-4 h-4 mr-1" /> Budget
                  </Button>
                </DialogTrigger>
                <DialogContent className={DLG}>
                  <DialogHeader><DialogTitle className="font-display tracking-tight text-white">Burndown configuration</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={LBL}>Budget hours</Label>
                        <Input type="number" min="0" step="1" className={FIELD} data-testid="budget-hours-input" value={budgetForm.budget_hours} onChange={(e) => setBudgetForm({ ...budgetForm, budget_hours: e.target.value })} />
                      </div>
                      <div>
                        <Label className={LBL}>Budget cash (£)</Label>
                        <Input type="number" min="0" step="100" className={FIELD} data-testid="budget-cash-input" value={budgetForm.budget_cash} onChange={(e) => setBudgetForm({ ...budgetForm, budget_cash: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={LBL}>Start date</Label>
                        <Input type="date" className={FIELD} data-testid="budget-start-input" value={budgetForm.burndown_start_date} onChange={(e) => setBudgetForm({ ...budgetForm, burndown_start_date: e.target.value })} />
                      </div>
                      <div>
                        <Label className={LBL}>Duration (days)</Label>
                        <Input type="number" min="1" max="365" className={FIELD} data-testid="budget-duration-input" value={budgetForm.burndown_duration_days} onChange={(e) => setBudgetForm({ ...budgetForm, burndown_duration_days: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={saveBudget} className={BTN_TEAL} data-testid="confirm-budget">Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={logOpen} onOpenChange={setLogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="rounded-md bg-[#00F5D4] hover:bg-[#00F5D4]/90 text-[#030712] font-bold font-mono shadow-glow-teal-md"
                    data-testid="open-log-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Log time
                  </Button>
                </DialogTrigger>
                <DialogContent className={DLG}>
                  <DialogHeader><DialogTitle className="font-display tracking-tight text-white">Log time</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={LBL}>Hours</Label>
                        <Input type="number" min="0" step="0.25" className={`${FIELD} text-2xl font-mono font-bold h-14`} data-testid="log-hours-input" value={logForm.hours_spent} onChange={(e) => setLogForm({ ...logForm, hours_spent: e.target.value })} />
                      </div>
                      <div>
                        <Label className={LBL}>Hourly rate (£)</Label>
                        <Input type="number" min="0" step="5" className={`${FIELD} h-14`} placeholder="0 = unbilled" data-testid="log-rate-input" value={logForm.hourly_billing_rate} onChange={(e) => setLogForm({ ...logForm, hourly_billing_rate: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={LBL}>Date</Label>
                        <Input type="date" className={FIELD} data-testid="log-date-input" value={logForm.date_logged} onChange={(e) => setLogForm({ ...logForm, date_logged: e.target.value })} />
                      </div>
                      <div>
                        <Label className={LBL}>Task (optional)</Label>
                        <Select value={logForm.task_id} onValueChange={(v) => setLogForm({ ...logForm, task_id: v })}>
                          <SelectTrigger className={FIELD} data-testid="log-task-select"><SelectValue placeholder="No task" /></SelectTrigger>
                          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                            <SelectItem value="__none__">— No task —</SelectItem>
                            {tasks.map((t) => <SelectItem key={t.task_id} value={t.task_id}>{t.title}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className={LBL}>Notes</Label>
                      <Textarea className={FIELD} data-testid="log-notes-input" value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer" data-testid="save-rate-default-wrap">
                      <Checkbox checked={logForm.save_as_default} onCheckedChange={(c) => setLogForm({ ...logForm, save_as_default: !!c })} data-testid="save-rate-default-cb" />
                      Save this rate (£{logForm.hourly_billing_rate || 0}) as my default for next time
                    </label>
                    <div className="text-sm bg-[#030712] border border-[#00F5D4]/20 px-3 py-2 font-mono text-[#00F5D4] rounded-md">
                      Burn: £{((Number(logForm.hours_spent) || 0) * (Number(logForm.hourly_billing_rate) || 0)).toFixed(2)}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={logTime}
                      className="w-full rounded-md bg-[#00F5D4] hover:bg-[#00F5D4]/90 text-[#030712] font-bold font-mono h-12 text-base shadow-glow-teal-md"
                      data-testid="confirm-log"
                    >
                      Record time
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-md bg-[#111827] border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10" data-testid="open-expense-btn">
                    <Plus className="w-4 h-4 mr-1" /> Log expense
                  </Button>
                </DialogTrigger>
                <DialogContent className={DLG}>
                  <DialogHeader>
                    <DialogTitle className="font-display tracking-tight text-white">Log expense</DialogTitle>
                    <p className="text-xs text-slate-500 mt-1">Non-labor cost (travel, software, contractor invoice…). Deducted from the cash budget alongside time-log labor cost.</p>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={LBL}>Amount (£)</Label>
                        <Input type="number" min="0" step="0.01" className={FIELD} data-testid="expense-amount-input" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
                      </div>
                      <div>
                        <Label className={LBL}>Category</Label>
                        <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                          <SelectTrigger className={FIELD} data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="travel">Travel</SelectItem>
                            <SelectItem value="software">Software / SaaS</SelectItem>
                            <SelectItem value="equipment">Equipment</SelectItem>
                            <SelectItem value="contractor">Contractor invoice</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className={LBL}>Date incurred</Label>
                      <Input type="date" className={FIELD} data-testid="expense-date-input" value={expenseForm.date_incurred} onChange={(e) => setExpenseForm({ ...expenseForm, date_incurred: e.target.value })} />
                    </div>
                    <div>
                      <Label className={LBL}>Description</Label>
                      <Textarea className={FIELD} data-testid="expense-description-input" placeholder="e.g. London client visit — taxis + lunch" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} />
                    </div>
                    <div className="text-xs bg-[#F59E0B]/8 border border-[#F59E0B]/25 px-3 py-2 font-mono text-[#F59E0B] rounded-md">
                      Deducts £{(Number(expenseForm.amount) || 0).toFixed(2)} from cash budget on {expenseForm.date_incurred}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={logExpense} className="rounded-md bg-[#F59E0B]/15 hover:bg-[#F59E0B]/25 border border-[#F59E0B]/30 text-[#F59E0B] font-mono" data-testid="confirm-expense">Record expense</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          }
        />

        <div className="px-4 sm:px-8 lg:px-12 py-6 space-y-5">
          {/* Project picker */}
          <div className="flex items-center gap-3">
            <Label className={LBL}>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-72 rounded-md bg-[#111827] border-[#1F2937] text-slate-100" data-testid="burndown-project-select"><SelectValue placeholder="Pick project" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* ── Alert banner (styled for wireframe) ─────────────────────────── */}
          {margin && margin.configured && (
            <div
              className={`flex items-start gap-3 px-5 py-4 border-l-2 ${
                bannerAlert
                  ? "border-[#F59E0B] bg-[#F59E0B]/10"
                  : "border-[#00F5D4] bg-[#00F5D4]/8"
              }`}
              data-testid="margin-banner"
            >
              <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${bannerAlert ? "text-[#F59E0B]" : "text-[#00F5D4]"}`} />
              <div className="flex-1">
                <div className={`text-[10px] uppercase tracking-[0.25em] font-bold font-mono ${bannerAlert ? "text-[#F59E0B]" : "text-[#00F5D4]"}`}>
                  {bannerAlert ? "Margin alert" : "Margin healthy"}
                </div>
                <div className="text-sm mt-1 text-slate-200" data-testid="margin-alert-message">
                  {eacData
                    ? `Projected ${eacData.projected_margin.toFixed(1)}% vs ${eacData.target_margin}% target`
                    : margin.alert_message}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-mono text-slate-400">
                  <span>Contract: £{Number(margin.contract_value).toLocaleString()}</span>
                  <span>Target margin: {margin.target_margin_percentage}%</span>
                  <span>Realised cost: £{Number(margin.total_cost_incurred).toLocaleString()}</span>
                  <span className={bannerAlert ? "text-[#F59E0B] font-bold" : "text-[#00F5D4]"}>
                    Current margin: {margin.current_margin_percentage}%
                  </span>
                </div>
              </div>
            </div>
          )}
          {margin && !margin.configured && project && (
            <div className="border border-dashed border-[#1F2937] px-4 py-3 text-xs text-slate-500 flex items-center gap-2 rounded-md" data-testid="margin-unconfigured">
              <AlertTriangle className="w-3.5 h-3.5 text-slate-600" />
              No commercial guardrails on this project. Set <code className="font-mono text-slate-400">contract_value</code> and <code className="font-mono text-slate-400">target_margin_percentage</code> via SOW ingest or project settings to enable real-time margin alerts.
            </div>
          )}

          {/* ── EAC Drill-down — two-column layout ──────────────────────────── */}
          {eacData && project && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* LEFT 60% */}
              <div className="lg:col-span-3 space-y-4">
                {/* Margin Forecast card */}
                <div className="border border-[#1F2937] bg-[#111827] p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Margin Forecast</div>
                  <MarginBar projected={eacData.projected_margin} target={eacData.target_margin} />
                  <div className="mt-5 grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#6B7280]">Projected at completion</div>
                      <div
                        className="font-display font-black text-2xl tracking-tighter mt-1"
                        style={{
                          color: eacData.projected_margin < eacData.target_margin - 15
                            ? "#FF007A"
                            : eacData.projected_margin >= eacData.target_margin - 5
                            ? "#00C896"
                            : "#F59E0B",
                        }}
                      >
                        {eacData.projected_margin.toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#6B7280]">Target</div>
                      <div className="font-display font-black text-2xl tracking-tighter mt-1 text-white">
                        {eacData.target_margin}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#6B7280]">Erosion</div>
                      <div
                        className="font-display font-black text-2xl tracking-tighter mt-1"
                        style={{ color: eacData.target_margin - eacData.projected_margin > 0 ? "#FF007A" : "#00C896" }}
                      >
                        {eacData.target_margin - eacData.projected_margin > 0 ? "−" : "+"}
                        {Math.abs(eacData.target_margin - eacData.projected_margin).toFixed(1)}pts
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown card */}
                <div className="border border-[#1F2937] bg-[#111827] p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">
                    Cost Breakdown · £{eacData.cost_to_date.toLocaleString()} to date
                  </div>
                  {eacData.cost_to_date > 0 ? (
                    <>
                      <div className="h-6 flex overflow-hidden rounded-sm">
                        <div
                          className="h-full"
                          style={{
                            width: `${(eacData.labour_cost / eacData.cost_to_date) * 100}%`,
                            backgroundColor: "#00C896",
                          }}
                        />
                        <div
                          className="h-full"
                          style={{
                            width: `${(eacData.expenses_cost / eacData.cost_to_date) * 100}%`,
                            backgroundColor: "#3B82F6",
                          }}
                        />
                      </div>
                      <div className="flex gap-5 mt-3 text-xs font-mono text-slate-400">
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#00C896]" />
                          Labour £{eacData.labour_cost.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#3B82F6]" />
                          Expenses £{eacData.expenses_cost.toLocaleString()}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-slate-500">No costs logged yet.</div>
                  )}
                </div>
              </div>

              {/* RIGHT 40% */}
              <div className="lg:col-span-2 space-y-4">
                {/* Forecast Inputs panel */}
                <div className="border border-[#1F2937] bg-[#111827] p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Forecast Inputs</div>
                  <div className="space-y-2 text-xs font-mono">
                    {[
                      { label: "Contract value",  value: `£${eacData.contract_value.toLocaleString()}` },
                      { label: "Target margin",   value: `${eacData.target_margin}%` },
                      { label: "Cost to date",    value: `£${eacData.cost_to_date.toLocaleString()}` },
                      { label: "Pct complete",    value: `${(eacData.pct_complete * 100).toFixed(1)}%` },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex justify-between items-center py-1 border-b border-[#1F2937]">
                        <span className="text-slate-500 uppercase tracking-[0.1em] text-[10px]">{label}</span>
                        <span className="text-slate-200">{value}</span>
                      </div>
                    ))}
                  </div>
                  {/* EAC formula */}
                  <div className="mt-4 bg-[#030712] border border-[#1F2937] p-3 rounded text-[10px] font-mono text-[#6B7280] leading-relaxed">
                    <div className="text-[#9CA3AF] mb-1">EAC cost = cost ÷ % complete</div>
                    <div>= £{eacData.cost_to_date.toLocaleString()} ÷ {(eacData.pct_complete * 100).toFixed(1)}% = £{Math.round(eacData.eac).toLocaleString()}</div>
                    <div className="mt-1 text-[#9CA3AF]">Projected margin = (value − EAC) ÷ value</div>
                    <div>= {eacData.projected_margin.toFixed(1)}%</div>
                  </div>
                  <p className="mt-3 text-[10px] text-slate-500 italic leading-relaxed">
                    Linear estimate-at-completion — assumes remaining work burns at the same rate as work to date.
                  </p>
                </div>

                {/* Check-in History panel */}
                <div className="border border-[#1F2937] bg-[#111827] p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Check-in History</div>
                  {logs.length === 0 ? (
                    <div className="text-xs text-slate-500">No time logged yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {logs.slice(0, 4).map((l) => (
                        <div key={l.log_id} className="flex items-center gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00F5D4] flex-shrink-0" />
                          <span className="text-xs font-mono text-slate-300 flex-1">
                            {l.hours_spent}h logged
                            {l.notes && <span className="text-slate-500"> · {l.notes.slice(0, 30)}{l.notes.length > 30 ? "…" : ""}</span>}
                          </span>
                          <span className="text-[10px] font-mono text-[#6B7280]">{fmtDate(l.date_logged)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Explainable Forecast — canonical EAC engine (Phase 1 spec, Unit A) */}
          {projectId && project && (
            <ForecastExplainPanel
              projectId={projectId}
              targetMarginPct={margin?.target_margin_percentage || project.target_margin_percentage || 0}
              canCompute={user?.role !== "viewer"}
            />
          )}

          {/* AI Recovery Playbook */}
          {margin && margin.configured && projectId && (
            <RecoveryPlaybookPanel
              projectId={projectId}
              canGenerate={user?.role !== "viewer"}
              driftExceeded={
                (Number(margin.target_margin_percentage) - Number(margin.current_margin_percentage)) >= DRIFT_THRESHOLD_CRITICAL
              }
            />
          )}

          {!burndown ? (
            <div className="p-12 text-sm text-slate-500 font-mono">Loading…</div>
          ) : burndown.budget_hours === 0 && burndown.budget_cash === 0 ? (
            <div className="border border-dashed border-[#1F2937] p-10 text-center rounded-md" data-testid="no-budget-state">
              <Settings2 className="w-8 h-8 mx-auto mb-3 text-slate-700" />
              <div className="text-sm text-slate-200 font-medium">No burndown budget set for this project</div>
              <div className="text-xs text-slate-500 mt-1">Click <strong className="text-[#00F5D4]">Budget</strong> to set initial hours, cash, start date, and duration.</div>
            </div>
          ) : (
            <>
              {/* Member filter */}
              {burndown.members?.length > 0 && (
                <div className="border border-[#1F2937] bg-[#111827] p-4 rounded-md" data-testid="member-filter">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-3.5 h-3.5 text-slate-500" />
                    <div className={LBL}>Filter by member</div>
                    {memberFilter.length > 0 && (
                      <button onClick={() => setMemberFilter([])} className="text-[10px] uppercase tracking-[0.2em] text-[#00F5D4] hover:underline ml-2" data-testid="clear-member-filter">
                        Clear ({memberFilter.length})
                      </button>
                    )}
                    <div className="ml-auto text-[10px] text-slate-500 font-mono">
                      {memberFilter.length === 0 ? "Showing whole team" : `Filtering ${memberFilter.length} of ${burndown.members.length} members`}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {burndown.members.map((m) => {
                      const active = memberFilter.includes(m.user_id);
                      return (
                        <button
                          key={m.user_id}
                          onClick={() => toggleMember(m.user_id)}
                          className={`flex items-center gap-2 px-3 py-1.5 border transition text-xs rounded ${active ? "border-[#00F5D4]/40 bg-[#00F5D4]/10 text-[#00F5D4]" : "border-[#1F2937] bg-[#030712] text-slate-400 hover:border-slate-600"}`}
                          data-testid={`member-pill-${m.user_id}`}
                        >
                          <UserCircle2 className="w-3.5 h-3.5" />
                          <span className="font-medium">{m.user_name}</span>
                          <span className="font-mono text-slate-500">
                            {m.hours}h · £{m.cash.toLocaleString()}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* KPI row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2937] border border-[#1F2937] rounded-md overflow-hidden">
                <KpiTile label="Hours remaining" value={`${burndown.hours_remaining.toLocaleString()}h`} sub={`${totals?.hoursPct ?? 0}% burned of ${burndown.budget_hours}h`} icon={Clock} accent="#00F5D4" alert={burndown.is_over_hours_budget} testid="kpi-hours-remaining" />
                <KpiTile label="Cash remaining" value={`£${burndown.cash_remaining.toLocaleString()}`} sub={`${totals?.cashPct ?? 0}% burned of £${burndown.budget_cash.toLocaleString()}`} icon={PoundSterling} accent="#00F5D4" alert={burndown.is_over_cash_budget} testid="kpi-cash-remaining" />
                <KpiTile label="Cash burned" value={`£${burndown.total_cash_burned.toLocaleString()}`} sub={`labor £${(burndown.total_cash_burned - (burndown.total_expenses_burned || 0)).toLocaleString()} + expenses £${(burndown.total_expenses_burned || 0).toLocaleString()}`} icon={TrendingDown} accent="#F9FAFB" testid="kpi-cash-burned" />
                <KpiTile label="Expenses logged" value={`£${(burndown.total_expenses_burned || 0).toLocaleString()}`} sub={`across ${expenses.length} item(s)`} icon={Receipt} accent="#F59E0B" testid="kpi-expenses-burned" />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1F2937] border border-[#1F2937] rounded-md overflow-hidden">
                <BurnChart
                  title="Hours burndown"
                  data={burndown.plot}
                  actualKey="hours_remaining"
                  idealKey="ideal_hours_remaining"
                  color="#00F5D4"
                  yLabel="Hours"
                  budget={burndown.budget_hours}
                  testid="chart-hours"
                />
                <BurnChart
                  title="Cash burndown (£)"
                  data={burndown.plot}
                  actualKey="financial_budget_remaining"
                  idealKey="ideal_cash_remaining"
                  color="#F59E0B"
                  yLabel="£"
                  budget={burndown.budget_cash}
                  testid="chart-cash"
                />
              </div>
            </>
          )}

          {/* Time log table */}
          <div className="border border-[#1F2937] rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-[#1F2937] flex items-center gap-2 bg-[#111827]">
              <Clock className="w-4 h-4 text-slate-500" />
              <div className="font-display font-bold tracking-tight text-sm text-white">Time log <span className="text-slate-600 font-normal">({logs.length})</span></div>
            </div>
            {logs.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500 bg-[#111827]" data-testid="empty-logs">No time logged yet. Click "Log time" above.</div>
            ) : (
              <div className="overflow-x-auto bg-[#111827]">
                <table className="w-full text-sm min-w-[720px]" data-testid="time-logs-table">
                  <thead className="bg-gray-100 text-[10px] uppercase tracking-[0.2em] text-gray-600 font-mono">
                    <tr>
                      <th className="text-left px-4 py-2">Date</th>
                      <th className="text-left px-4 py-2">Logger</th>
                      <th className="text-left px-4 py-2">Task</th>
                      <th className="text-right px-4 py-2">Hours</th>
                      <th className="text-right px-4 py-2">Rate</th>
                      <th className="text-right px-4 py-2">Burn</th>
                      <th className="text-left px-4 py-2">Notes</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => {
                      const own = user && l.user_id === user.user_id;
                      const canDel = own || user?.role === "owner" || user?.role === "admin";
                      return (
                        <tr key={l.log_id} className="border-t border-[#1F2937]/60 hover:bg-[#1F2937]/20 transition-colors" data-testid={`log-row-${l.log_id}`}>
                          <td className="px-4 py-2 font-mono text-xs text-slate-400">{l.date_logged}</td>
                          <td className="px-4 py-2 text-xs text-slate-200">{l.user_name}</td>
                          <td className="px-4 py-2 text-xs text-slate-400 truncate max-w-[180px]">
                            {l.task_title ? <span className="bg-[#030712] border border-[#1F2937] px-1.5 py-0.5 rounded">{l.task_title}</span> : <span className="text-slate-700">—</span>}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-slate-200">{l.hours_spent}h</td>
                          <td className="px-4 py-2 text-right font-mono text-slate-500">£{(l.hourly_billing_rate || l.hourly_billing_rate_at_time_of_log || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-right font-mono font-semibold text-[#00F5D4]">£{(l.realized_financial_burn || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-xs text-slate-400 truncate max-w-xs">{l.notes}</td>
                          <td className="px-2 py-2">
                            {canDel && (
                              <button onClick={() => deleteLog(l.log_id)} className="text-slate-600 hover:text-[#FF007A] transition-colors" data-testid={`delete-log-${l.log_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Expenses table */}
          <div className="border border-[#1F2937] rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-[#1F2937] flex items-center gap-2 bg-[#111827]">
              <Receipt className="w-4 h-4 text-slate-500" />
              <div className="font-display font-bold tracking-tight text-sm text-white">Expenses <span className="text-slate-600 font-normal">({expenses.length})</span></div>
              <div className="ml-auto text-[11px] font-mono text-[#F59E0B]">
                Total: £{(burndown?.total_expenses_burned || 0).toLocaleString()}
              </div>
            </div>
            {expenses.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500 bg-[#111827]" data-testid="empty-expenses">
                No expenses logged yet. Click &ldquo;Log expense&rdquo; above to deduct travel, software, contractor invoices, etc. from the cash budget.
              </div>
            ) : (
              <div className="overflow-x-auto bg-[#111827]">
                <table className="w-full text-sm min-w-[720px]" data-testid="expenses-table">
                  <thead className="bg-gray-100 text-[10px] uppercase tracking-[0.2em] text-gray-600 font-mono">
                    <tr>
                      <th className="text-left px-4 py-2">Date</th>
                      <th className="text-left px-4 py-2">Logger</th>
                      <th className="text-left px-4 py-2">Category</th>
                      <th className="text-right px-4 py-2">Amount</th>
                      <th className="text-left px-4 py-2">Description</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((ex) => {
                      const own = user && ex.user_id === user.user_id;
                      const canDel = own || user?.role === "owner" || user?.role === "admin";
                      return (
                        <tr key={ex.expense_id} className="border-t border-[#1F2937]/60 hover:bg-[#1F2937]/20 transition-colors" data-testid={`expense-row-${ex.expense_id}`}>
                          <td className="px-4 py-2 font-mono text-xs text-slate-400">{ex.date_incurred}</td>
                          <td className="px-4 py-2 text-xs text-slate-200">{ex.user_name}</td>
                          <td className="px-4 py-2 text-xs">
                            <span className="bg-[#F59E0B]/10 border border-[#F59E0B]/25 text-[#F59E0B] px-1.5 py-0.5 capitalize rounded">{ex.category}</span>
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-semibold text-[#F59E0B]">£{ex.amount.toLocaleString()}</td>
                          <td className="px-4 py-2 text-xs text-slate-400 truncate max-w-xs">{ex.description || <span className="text-slate-700">—</span>}</td>
                          <td className="px-2 py-2">
                            {canDel && (
                              <button onClick={() => deleteExpense(ex.expense_id)} className="text-slate-600 hover:text-[#FF007A] transition-colors" data-testid={`delete-expense-${ex.expense_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer note */}
          <div className="text-[10px] text-slate-600 font-mono border-t border-[#1F2937] pt-4 leading-relaxed">
            Margin shown is <strong className="text-slate-500">projected at completion</strong> (estimate-at-completion), not cost-to-date.
            Bar scale 0–50%. Hatched segment = forecast shortfall against target.
          </div>
        </div>
      </div>
    </DarkPage>
  );
};

const KpiTile = ({ label, value, sub, icon: Icon, accent, alert, testid }) => (
  <div className="bg-[#111827] p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <Icon className="w-4 h-4" style={{ color: alert ? "#FF007A" : "#475569" }} />
      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    </div>
    <div
      className="font-mono font-bold text-3xl tracking-tight mt-2"
      style={{ color: alert ? "#FF007A" : accent }}
    >
      {value}
    </div>
    <div className="text-[10px] text-slate-500 mt-1 font-mono">{sub}</div>
    {alert && <div className="text-[10px] text-[#FF007A] mt-1 flex items-center gap-1 font-mono"><AlertTriangle className="w-3 h-3" /> Over budget</div>}
  </div>
);

const TOOLTIP_STYLE = { borderRadius: 6, border: "1px solid #1F2937", fontSize: 12, background: "#111827", color: "#F9FAFB" };
const LEGEND_STYLE = { fontSize: 11 };
const ACTUAL_DOT = { r: 3 };
const ACTUAL_ACTIVE_DOT = { r: 5 };

const BurnChart = ({ title, data, actualKey, idealKey, color, yLabel, budget, testid }) => (
  <div className="bg-[#111827] p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">{title}</div>
      <div className="text-[10px] text-slate-600 font-mono">budget: {yLabel === "£" ? "£" : ""}{budget.toLocaleString()}{yLabel !== "£" ? "h" : ""}</div>
    </div>
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data}>
        <CartesianGrid stroke="#1F2937" />
        <XAxis dataKey="day_number" stroke="#64748b" fontSize={10} tickFormatter={(d) => `d${d}`} />
        <YAxis stroke="#64748b" fontSize={10} />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(v) => (yLabel === "£" ? `£${v.toLocaleString()}` : `${v}h`)}
          labelFormatter={(d, payload) => payload?.[0]?.payload?.date_label || `Day ${d}`}
        />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <ReferenceLine y={0} stroke="#475569" />
        <Line type="monotone" dataKey={idealKey} name="Ideal" stroke="#475569" strokeDasharray="4 4" dot={false} strokeWidth={1.5} />
        <Line type="monotone" dataKey={actualKey} name="Actual" stroke={color} strokeWidth={2.5} dot={ACTUAL_DOT} activeDot={ACTUAL_ACTIVE_DOT} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export default Burndown;
